import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

export interface FilaOption {
  key: string;
}

export interface FilaSelectorProps {
  value: string;
  options: FilaOption[];
  onChange: (value: string) => void;
}

export function FilaSelector({ value, options, onChange }: FilaSelectorProps) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClickOutside(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const select = (v: string): void => {
    onChange(v);
    setOpen(false);
  };

  const label = value === '' ? 'Todas as filas' : value;

  return (
    <div ref={wrapRef} className="relative inline-block flex-shrink-0">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="inline-flex items-center gap-2 whitespace-nowrap rounded-full border border-border bg-surface px-3 py-1 text-[12px] text-text hover:bg-bg focus:outline-none focus:ring-2 focus:ring-accent/40"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Filtrar por fila"
      >
        <span className="font-medium">{label}</span>
        <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 text-textMute" />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 z-30 mt-1 max-h-72 min-w-full max-w-[calc(100vw-1.5rem)] overflow-auto rounded-md border border-border bg-surface shadow-md"
        >
          <button
            type="button"
            role="option"
            aria-selected={value === ''}
            onClick={() => select('')}
            className="flex w-full items-center justify-between gap-3 whitespace-nowrap px-3 py-2 text-left text-sm hover:bg-bg"
          >
            <span>Todas as filas</span>
            {value === '' && (
              <span aria-hidden className="h-2 w-2 rounded-full bg-okDot" />
            )}
          </button>
          <div className="border-t border-borderSoft" />
          {options.map(o => {
            const isActive = o.key === value;
            return (
              <button
                key={o.key}
                type="button"
                role="option"
                aria-selected={isActive}
                onClick={() => select(o.key)}
                className="flex w-full items-center justify-between gap-3 whitespace-nowrap px-3 py-2 text-left text-sm hover:bg-bg"
              >
                <span className="text-text">{o.key}</span>
                {isActive && (
                  <span aria-hidden className="h-2 w-2 rounded-full bg-okDot" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default FilaSelector;
